// appUpdate.js — has the app been redeployed since this tab loaded it?
//
// A release replaces the bundle a tab is running and nothing in that tab notices: it goes on
// serving the old code, against an API that has already moved, until something makes it reload.
//
// index.html always names the CURRENT bundle, so fetching it and comparing its main script with
// the one we are running answers the question exactly. No commit has to be injected at build time,
// and it asks about the FRONTEND, which is the thing a reload actually replaces — a backend-only
// release needs nothing from the user, because the API it talks to is already the new one.
import { useCallback, useEffect, useRef, useState } from 'react';

// Long enough to be invisible, short enough that somebody who leaves a tab open all day is told.
const EVERY_MS = 5 * 60 * 1000;
const MAIN_JS = /\/static\/js\/(main\.[a-z0-9]+\.js)/i;

/** The bundle this tab is running, or null in development, where it has no hash to compare. */
export function runningBundle(doc = typeof document === 'undefined' ? null : document) {
  if (!doc) return null;
  for (const s of doc.querySelectorAll('script[src]')) {
    const m = MAIN_JS.exec(s.getAttribute('src') || '');
    if (m) return m[1];
  }
  return null;
}

/** The bundle index.html names right now, or null if it cannot be read. */
export async function deployedBundle(fetcher = fetch) {
  try {
    // no-store, and a changing query, because a cached index.html is exactly the stale answer.
    const res = await fetcher(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const m = MAIN_JS.exec(await res.text());
    return m ? m[1] : null;
  } catch {
    return null;        // offline, or a dev server with no built index: not an update
  }
}

/**
 * True once a different bundle is live. Checked every few minutes and whenever the tab is looked
 * at again, which is when somebody who left it open overnight comes back to it.
 */
export function useAppUpdate() {
  const [updated, setUpdated] = useState(false);
  const mine = useRef(runningBundle()).current;

  const check = useCallback(async () => {
    if (!mine || document.visibilityState === 'hidden') return;
    const live = await deployedBundle();
    if (live && live !== mine) setUpdated(true);
  }, [mine]);

  useEffect(() => {
    if (!mine) return undefined;          // development build: nothing to compare
    const id = setInterval(check, EVERY_MS);
    const onShow = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
    };
  }, [mine, check]);

  return updated;
}
